import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApolloClient,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  from,
  useMutation,
  useQuery,
} from '@apollo/client'
import { removeTypenameFromVariables } from '@apollo/client/link/remove-typename'
import { offsetLimitPagination } from '@apollo/client/utilities'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect as connectRedux, useSelector } from 'react-redux'
import { compose } from 'recompose'
import { map as mapPromise } from 'bluebird'
import _castArray from 'lodash/castArray'
import _defaultTo from 'lodash/defaultTo'
import _difference from 'lodash/difference'
import _filter from 'lodash/filter'
import _find from 'lodash/find'
import _get from 'lodash/get'
import _includes from 'lodash/includes'
import _intersectionWith from 'lodash/intersectionWith'
import _isArray from 'lodash/isArray'
import _isEmpty from 'lodash/isEmpty'
import _isEqual from 'lodash/isEqual'
import _isUndefined from 'lodash/isUndefined'
import _map from 'lodash/map'
import _matches from 'lodash/matches'
import _omit from 'lodash/omit'
import _parseInt from 'lodash/parseInt'
import _pick from 'lodash/pick'
import _reduce from 'lodash/reduce'
import _set from 'lodash/set'
import _some from 'lodash/some'
import _sortBy from 'lodash/sortBy'
import _split from 'lodash/split'
import { CONTENT_STORE_KEY, locationToOptions } from 'services/content'
import {
  CONTENT_REFERENCE_TYPE_CENTERS,
  CONTENT_REFERENCE_TYPE_MARKETING_CENTERS,
  CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER,
  CONTENT_REFERENCE_TYPE_MEDITATION_DURATION,
  CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS,
  CONTENT_REFERENCE_TYPE_MEDITATION_STYLE,
  CONTENT_REFERENCE_TYPE_YOGA_TEACHER,
  CONTENT_REFERENCE_TYPE_YOGA_DURATION,
  CONTENT_REFERENCE_TYPE_YOGA_FOCUS,
  CONTENT_REFERENCE_TYPE_YOGA_STYLE,
  CONTENT_REFERENCE_TYPE_YOGA_LEVEL,
  CONTENT_REFERENCE_TYPE_INTERVIEW_HOST,
  CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST,
  CONTENT_REFERENCE_TYPE_CATEGORY,
  CONTENT_REFERENCE_TYPE_PRIMARY_COURSE,
  CONTENT_REFERENCE_TYPE_PRODUCT,
  CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY,
  CONTENT_REFERENCE_TYPE_SERIES,
  CONTENT_REFERENCE_TYPES,
  CONTENT_REFERENCE_TYPE_GENRES,
  CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE,
  CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN,
} from 'services/content-references/types'
import { getViewError } from 'services/data'
import { createAuthorization } from 'services/auth'
import { selectConfig } from 'services/config/redux/selectors'
import { selectAuth } from 'services/auth/redux/selectors'
import { getLocationQuery } from 'services/location'
import Container from 'muicss/lib/react/container'
import {
  DataGridPro,
  GridRowEditStopReasons,
  GridRowEditStartReasons,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from '@mui/x-data-grid-pro'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import { transformOperator } from '@/services/mui-grid-pro'
import { createPrioritySortedColumns, createNewColumnOrder } from './helpers'
import { buildColumns, COURSE_FILTER_FIELD } from './columns'
import { useContentReferenceOptions } from './useContentReferenceOptions'
import { useCourseFilter } from './useCourseFilter'
import {
  CONTENT_LIST_QUERY,
  CONTENT_UPDATE_MUTATION,
  CONTENT_REFERENCE_CREATE_MUTATION,
  CONTENT_REFERENCE_DELETE_MUTATION,
} from './queries'
import {
  DEFAULT_LANGUAGE_SELECTION,
  LANGUAGE_SELECTION_OPTIONS,
  SNACKBAR_DEFAULT_DURATION,
  SORT_DEFAULT,
  COLUMN_DEFAULT_VISIBILITY,
  COLUMN_DEFAULT_ORDER,
  COLUMN_DEFAULT_PINS,
} from './constants'
import './index.scss'

function paginationModelToOffsetLimit (paginationModel) {
  const { page, pageSize } = paginationModel
  return {
    offset: page * pageSize,
    limit: pageSize,
  }
}
function modelsToOrder (filterModel, sortModel) {
  const references = _filter(
    filterModel.items,
    (i) => CONTENT_REFERENCE_TYPES.includes(i.field) && !_isEmpty(i.value),
  )
  return {
    order: _some(references, _matches({ field: 'series' }))
      ? [
          ['seasonNumber', 'asc'],
          ['episodeNumber', 'asc'],
        ]
      : _map(sortModel, ({ field, sort }) => [field, sort]),
  }
}
function filterModelToWhere (filterModel, languageSelection) {
  const contentReferencesFilterKey =
    filterModel?.logicOperator === 'or' ? 'references' : 'referencesEvery'
  const items = _filter(
    filterModel.items,
    (i) =>
      !CONTENT_REFERENCE_TYPES.includes(i.field) &&
      i.field !== COURSE_FILTER_FIELD &&
      !_isEmpty(i.value),
  )
  const references = _filter(
    filterModel.items,
    (i) => CONTENT_REFERENCE_TYPES.includes(i.field) && !_isEmpty(i.value),
  )

  // Extract contentProviderProvider filter if present
  const providerFilter = _find(items, { field: 'contentProviderProvider' })
  let contentProviders = []
  let contentProvidersNot = []
  let filteredItems = items
  if (providerFilter) {
    const { operator, value } = providerFilter
    const isNonEmpty = Array.isArray(value)
      ? value.length > 0 && value.some((v) => v !== '')
      : value !== '' && value !== undefined && value !== null
    if (isNonEmpty) {
      if (operator === 'equals') {
        contentProviders = Array.isArray(value)
          ? value.filter((v) => v !== '').map((v) => ({ provider: v }))
          : [{ provider: value }]
      } else if (operator === 'notEquals') {
        contentProvidersNot = Array.isArray(value)
          ? value.filter((v) => v !== '').map((v) => ({ provider: v }))
          : [{ provider: value }]
      }
    }
    // Remove from items so it's not included in where
    filteredItems = _filter(items, (i) => i.field !== 'contentProviderProvider')
  }

  const buildWhereQueryItem = (item) => {
    const { field, operator, value } = item
    const graphQLOperator = transformOperator(operator)
    switch (field) {
      case 'contentId':
        return {
          [graphQLOperator]: _isArray(value) ? _map(value, _parseInt) : _parseInt(value),
        }
      case 'contentPolicyPolicyId':
        return {
          [graphQLOperator]: _isArray(value) ? _map(value, _parseInt) : _parseInt(value),
        }
      case 'publishedAtDate':
        return {
          [graphQLOperator]: new Date(value).toISOString(),
        }
      case 'published':
        return {
          [graphQLOperator]: value === 'true',
        }
      default:
        return {
          [graphQLOperator]: _includes(['like', 'ilike'], graphQLOperator) ? `%${value}%` : value,
        }
    }
  }
  return {
    where: !_isEmpty(filteredItems)
      ? _reduce(
          filteredItems,
          (acc, i) => {
            acc[i.field] = {
              ...acc[i.field],
              ...buildWhereQueryItem(i),
            }
            return acc
          },
          { language: { in: languageSelection } },
        )
      : { language: { in: languageSelection } },
    [contentReferencesFilterKey]: !_isEmpty(references)
      ? _reduce(
          references,
          (acc, r) => {
            const { field, value } = r
            return [
              ...acc,
              ..._map(_castArray(value), (v) => {
                const [contentId, contentType] = _split(v, '-')
                return {
                  contentId: _parseInt(contentId),
                  contentType,
                  type: field,
                }
              }),
            ]
          },
          [],
        )
      : [],
    ...(contentProviders.length > 0 ? { contentProviders } : {}),
    ...(contentProvidersNot.length > 0 ? { contentProvidersNot } : {}),
  }
}

function CustomToolbar (props) {
  const { filterModel, setFilterModel, languageSelection, setLanguageSelection } = props
  const contentIdSearchValue =
    _find(filterModel.items, { field: 'contentId', operator: '=' })?.value || ''
  const titleSearchValue =
    _find(filterModel.items, { field: 'title', operator: 'contains' })?.value || ''
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <Box sx={{ flexGrow: 1 }} />
      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
        <TextField
          id="title-search"
          label="Title"
          size="small"
          type="search"
          value={titleSearchValue}
          onChange={(e) =>
            setFilterModel({
              items: [
                // Enable this once the datagrid pro version has be enabled
                // ..._filter(filterModel.items, ({ field }) => field !== 'title'),
                {
                  field: 'title',
                  operator: 'contains',
                  value: e.target.value,
                },
              ],
            })
          }
        />
      </FormControl>
      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
        <TextField
          id="content-id-search"
          label="Content ID"
          size="small"
          type="search"
          value={contentIdSearchValue}
          onChange={(e) =>
            setFilterModel({
              items: [
                // Enable this once the datagrid pro version has be enabled
                // ..._filter(filterModel.items, ({ field }) => field !== 'contentId'),
                {
                  field: 'contentId',
                  operator: '=',
                  value: e.target.value,
                },
              ],
            })
          }
        />
      </FormControl>
      <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
        <InputLabel id="language-select-label">Language</InputLabel>
        <Select
          labelId="language-select-label"
          id="language-select"
          value={languageSelection.toString()}
          label="Language"
          onChange={(e) => {
            const newLanguageSelection = e.target.value.split(',')
            setLanguageSelection(newLanguageSelection)
          }}
        >
          {_map(LANGUAGE_SELECTION_OPTIONS, ({ label, value }) => (
            <MenuItem key={value} value={value.toString()}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </GridToolbarContainer>
  )
}

function ContentListRows (props) {
  const [languageSelection, setLanguageSelection] = useState(DEFAULT_LANGUAGE_SELECTION)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })
  const [sortModel, setSortModel] = useState(SORT_DEFAULT)
  const [filterModel, setFilterModel] = useState({ items: [] })
  const [rowModesModel, setRowModesModel] = useState({})
  const [contentUpdate] = useMutation(CONTENT_UPDATE_MUTATION)
  const [contentReferenceCreate] = useMutation(CONTENT_REFERENCE_CREATE_MUTATION)
  const [contentReferenceDelete] = useMutation(CONTENT_REFERENCE_DELETE_MUTATION)
  const [snackbar, setSnackbar] = useState(null)
  const [rowCountState, setRowCountState] = useState(data?.contentList?.count || 0)
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(COLUMN_DEFAULT_VISIBILITY)
  const [columnOrder, setColumnOrder] = useState(COLUMN_DEFAULT_ORDER)
  const [pinnedColumns, setPinnedColumns] = useState(COLUMN_DEFAULT_PINS)
  const [locationPathname, setLocationPathname] = useState('')

  const { courseReferenceEntries, courseModulesLoading, courseContentId } =
    useCourseFilter(filterModel)

  const contentListVariables = useMemo(() => {
    const base = {
      ...paginationModelToOffsetLimit(paginationModel),
      ...filterModelToWhere(filterModel, languageSelection),
      ...modelsToOrder(filterModel, sortModel),
    }
    if (_isEmpty(courseReferenceEntries)) return base
    return {
      ...base,
      references: [...(base.references || []), ...courseReferenceEntries],
      referencesEvery: base.referencesEvery,
    }
  }, [paginationModel, filterModel, sortModel, languageSelection, courseReferenceEntries])

  const skipContentListForCourse = Boolean(courseContentId && courseModulesLoading)

  const { loading, data } = useQuery(CONTENT_LIST_QUERY, {
    variables: contentListVariables,
    skip: skipContentListForCourse,
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      displaySnackbar(error.message, 'error', true)
    },
  })

  const contentReferenceOptions = useContentReferenceOptions(languageSelection)

  const displaySnackbar = (message, severity = 'info', persist = false) => {
    const snackbarOptions = {
      message,
      severity,
      duration: persist ? null : SNACKBAR_DEFAULT_DURATION,
    }
    setSnackbar(snackbarOptions)
  }
  const handleSnackbarClose = () => setSnackbar(null)
  const handleRowEditStart = (params, event) => {
    if (_includes(GridRowEditStartReasons, params.reason)) {
      _set(event, 'defaultMuiPrevented', true)
    }
  }
  const handleRowEditStop = (params, event) => {
    if (_includes(GridRowEditStopReasons, params.reason)) {
      _set(event, 'defaultMuiPrevented', true)
    }
  }

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel)
  }
  const processRowUpdate = useCallback(
    async (updateRow, originalRow) => {
      const updateReferences = _sortBy(_get(updateRow, 'contentReferencesFrom'))
      const originalReferences = _sortBy(_get(originalRow, 'contentReferencesFrom'))
      if (_isEqual(updateRow, originalRow)) {
        displaySnackbar('No changes made')
        return originalRow
      }
      // create/delete content references before updating the content fields so that the content
      // references are updated in the response to the content update mutation
      if (!_isEqual(updateReferences, originalReferences)) {
        const uniqueUpdateReferences = _difference(updateReferences, originalReferences)
        const uniqueOriginalReferences = _difference(originalReferences, updateReferences)
        const titleGroupIdsToSkip = _map(
          _intersectionWith(
            uniqueUpdateReferences,
            uniqueOriginalReferences,
            (toCreate, toDelete) =>
              _isEqual(_get(toCreate, ['contentTo', 0]), _get(toDelete, ['contentTo', 0])),
          ),
          (r) => _get(r, ['contentTo', 0, 'titleGroupId']),
        )
        // delete references that are no longer present
        await mapPromise(
          _filter(
            uniqueOriginalReferences,
            (r) => !_includes(titleGroupIdsToSkip, _get(r, ['contentTo', 0, 'titleGroupId'])),
          ),
          async ({ id }) => {
            contentReferenceDelete({ variables: { contentReference: { id } } })
          },
        )
        // create references that are new
        await mapPromise(
          _filter(
            uniqueUpdateReferences,
            (r) => !_includes(titleGroupIdsToSkip, _get(r, ['contentTo', 0, 'titleGroupId'])),
          ),
          async (params) => {
            const { contentReferenceType, contentTo } = params
            const { type } = contentReferenceType
            const { contentId: fromContentContentId, contentType: fromContentContentType } =
              updateRow
            const [toContentContentId, toContentContentType] = _split(
              contentTo[0].titleGroupId,
              '-',
            )
            const contentReference = {
              fromContentContentId,
              fromContentContentType,
              toContentContentId: _parseInt(toContentContentId),
              toContentContentType,
              type,
            }
            contentReferenceCreate({ variables: { contentReference } })
          },
        )
      }
      const cleanedRow = _omit(updateRow, [
        'contentReferencesFrom',
        'contentAssetReferences',
        'contentProvider',
        'courseModuleRefs',
      ])
      const response = await contentUpdate({
        variables: {
          language: 'en', // get English results back
          content: cleanedRow,
          generatePath: true,
        },
      })
      const content = _get(response, 'data.contentUpdate.content', {})
      displaySnackbar('Content updated', 'success')
      return { ...content, courseModuleRefs: originalRow.courseModuleRefs }
    },
    [contentReferenceCreate, contentReferenceDelete, contentUpdate],
  )
  const handleProcessRowUpdateError = useCallback((rowUpdateError) => {
    displaySnackbar(rowUpdateError.message, 'error', true)
  }, [])

  useEffect(() => {
    setRowCountState((prevRowCountState) =>
      !_isUndefined(data?.contentList?.count) ? data.contentList.count : prevRowCountState,
    )
  }, [data?.contentList?.count, setRowCountState])

  useEffect(() => {
    const setPageUrlFromPreferences = (
      columnsOrder,
      filters,
      columnVisibility,
      languageForUrl,
      pinnedColumnsForUrl,
    ) => {
      let path = locationPathname
      const queryString = []
      if (!_isEmpty(columnsOrder) && !_isEqual(COLUMN_DEFAULT_ORDER, columnsOrder)) {
        queryString.push(`columns=${JSON.stringify(columnsOrder)}`)
      }
      if (!_isEmpty(filters)) {
        queryString.push(`filters=${JSON.stringify(filters)}`)
      }
      if (!_isEmpty(columnVisibility) && !_isEqual(columnVisibility, COLUMN_DEFAULT_VISIBILITY)) {
        queryString.push(`visibility=${JSON.stringify(columnVisibility)}`)
      }
      if (!_isEmpty(languageForUrl) && !_isEqual(languageForUrl, DEFAULT_LANGUAGE_SELECTION)) {
        queryString.push(`language=${JSON.stringify(languageForUrl)}`)
      }
      if (!_isEmpty(pinnedColumnsForUrl) && !_isEqual(pinnedColumnsForUrl, COLUMN_DEFAULT_PINS)) {
        queryString.push(`pinned=${JSON.stringify(pinnedColumnsForUrl)}`)
      }
      path += `${queryString.length > 0 ? '?' : ''}${queryString.join('&')}`
      window.history.pushState(null, null, path)
    }

    setPageUrlFromPreferences(
      columnOrder,
      filterModel?.items,
      columnVisibilityModel,
      languageSelection,
      pinnedColumns,
    )
  }, [
    columnOrder,
    filterModel,
    columnVisibilityModel,
    languageSelection,
    locationPathname,
    pinnedColumns,
  ])

  useEffect(() => {
    setLocationPathname(props.location.getIn(['data', 'pathname'], ''))

    try {
      const columnQueryParam = getLocationQuery(props.location).get('columns', undefined)
      if (columnQueryParam) {
        setColumnOrder(JSON.parse(columnQueryParam))
      } else {
        setColumnOrder(COLUMN_DEFAULT_ORDER)
      }
    } catch (columnQueryError) {
      setColumnOrder(COLUMN_DEFAULT_ORDER)
      displaySnackbar('Error parsing url: Column Order Error. Reverting to default.', 'error', true)
    }

    try {
      const filterModelParam = getLocationQuery(props.location).get('filters', undefined)
      if (filterModelParam) {
        setFilterModel({ items: JSON.parse(filterModelParam) })
      }
    } catch (filterQueryError) {
      setFilterModel({ items: [] })
      displaySnackbar('Error parsing url: Filter Error. Reverting to default.', 'error', true)
    }

    try {
      const visibilityParam = getLocationQuery(props.location).get('visibility', undefined)
      if (visibilityParam) {
        setColumnVisibilityModel(JSON.parse(visibilityParam))
      }
    } catch (visibilityQueryError) {
      setColumnVisibilityModel({})
      displaySnackbar('Error parsing url: Visibility Error. Reverting to default.', 'error', true)
    }

    try {
      const pinnedParam = getLocationQuery(props.location).get('pinned', undefined)
      if (pinnedParam) {
        setPinnedColumns(JSON.parse(pinnedParam))
      }
    } catch (pinnedQueryError) {
      setPinnedColumns(COLUMN_DEFAULT_PINS)
      displaySnackbar('Error parsing url: Pinned Error. Reverting to default.', 'error', true)
    }

    try {
      const languageParam = getLocationQuery(props.location).get('language', undefined)
      if (languageParam) {
        const languageVal = JSON.parse(languageParam).map((val) => val.toLowerCase())
        const languageOptions = LANGUAGE_SELECTION_OPTIONS.map((option) => option.value)
        if (languageOptions.find((languageOption) => _isEqual(languageOption, languageVal))) {
          setLanguageSelection(languageVal)
        }
      }
    } catch (languageQueryError) {
      setLanguageSelection(DEFAULT_LANGUAGE_SELECTION)
      displaySnackbar('Error parsing url: Language Error. Reverting to default.', 'error', true)
    }
  }, [props.location])

  const rows = loading ? [] : _defaultTo(data?.contentList?.content, [])
  const columns = useMemo(
    () =>
      createPrioritySortedColumns(
        columnOrder,
        buildColumns({
          rowModesModel,
          setRowModesModel,
          displaySnackbar,
          seriesOptions: contentReferenceOptions[CONTENT_REFERENCE_TYPE_SERIES] || [],
          rainbowCategoryOptions:
            contentReferenceOptions[CONTENT_REFERENCE_TYPE_RAINBOW_CATEGORY] || [],
          centersOptions: contentReferenceOptions[CONTENT_REFERENCE_TYPE_CENTERS] || [],
          productOptions: contentReferenceOptions[CONTENT_REFERENCE_TYPE_PRODUCT] || [],
          genreOptions: contentReferenceOptions[CONTENT_REFERENCE_TYPE_GENRES] || [],
          thirdPartyGenreOptions:
            contentReferenceOptions[CONTENT_REFERENCE_TYPE_THIRD_PARTY_GENRE] || [],
          contentOriginOptions:
            contentReferenceOptions[CONTENT_REFERENCE_TYPE_CONTENT_ORIGIN] || [],
          interviewHostOptions:
            contentReferenceOptions[CONTENT_REFERENCE_TYPE_INTERVIEW_HOST] || [],
          interviewGuestOptions:
            contentReferenceOptions[CONTENT_REFERENCE_TYPE_INTERVIEW_GUEST] || [],
          categoryOptions: contentReferenceOptions[CONTENT_REFERENCE_TYPE_CATEGORY] || [],
          marketingCentersOptions:
            contentReferenceOptions[CONTENT_REFERENCE_TYPE_MARKETING_CENTERS] || [],
          meditationFocusOptions:
            contentReferenceOptions[CONTENT_REFERENCE_TYPE_MEDITATION_FOCUS] || [],
          meditationStyleOptions:
            contentReferenceOptions[CONTENT_REFERENCE_TYPE_MEDITATION_STYLE] || [],
          meditationTeacherOptions:
            contentReferenceOptions[CONTENT_REFERENCE_TYPE_MEDITATION_TEACHER] || [],
          meditationDurationOptions:
            contentReferenceOptions[CONTENT_REFERENCE_TYPE_MEDITATION_DURATION] || [],
          yogaDurationOptions: contentReferenceOptions[CONTENT_REFERENCE_TYPE_YOGA_DURATION] || [],
          yogaFocusOptions: contentReferenceOptions[CONTENT_REFERENCE_TYPE_YOGA_FOCUS] || [],
          yogaLevelOptions: contentReferenceOptions[CONTENT_REFERENCE_TYPE_YOGA_LEVEL] || [],
          yogaStyleOptions: contentReferenceOptions[CONTENT_REFERENCE_TYPE_YOGA_STYLE] || [],
          yogaTeacherOptions: contentReferenceOptions[CONTENT_REFERENCE_TYPE_YOGA_TEACHER] || [],
          primaryCourseOptions:
            contentReferenceOptions[CONTENT_REFERENCE_TYPE_PRIMARY_COURSE] || [],
        }),
      ),
    [columnOrder, rowModesModel, contentReferenceOptions],
  )

  const onColumnOrderChange = (columnOrderChange) => {
    setColumnOrder((prevColumnOrder) => createNewColumnOrder(prevColumnOrder, columnOrderChange))
  }

  const onPinnedColumnsChange = (pinnedColumnsChange) => {
    setPinnedColumns(pinnedColumnsChange)
  }

  const dataGridStyles = {
    '.MuiDataGrid-booleanCell[data-value="false"]': {
      color: 'red',
    },
    '.MuiDataGrid-booleanCell[data-value="true"]': {
      color: '#00bb00',
    },
  }

  return (
    <div style={{ height: 800, width: '100%' }}>
      <DataGridPro
        sx={dataGridStyles}
        rows={rows}
        columns={columns}
        ignoreDiacritics
        loading={loading}
        editMode="row"
        rowCount={rowCountState}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={handleProcessRowUpdateError}
        filterMode="server"
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
        onColumnOrderChange={onColumnOrderChange}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={onPinnedColumnsChange}
        filterModel={filterModel}
        onFilterModelChange={(newFilterModel) => setFilterModel(newFilterModel)}
        pagination
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={(newPaginationModel) => {
          setPaginationModel(newPaginationModel)
        }}
        slots={{ toolbar: CustomToolbar }}
        slotProps={{
          toolbar: {
            languageSelection,
            setLanguageSelection,
            filterModel,
            setFilterModel,
          },
        }}
        sortMode="server"
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
      />
      {!!snackbar && (
        <Snackbar
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          onClose={handleSnackbarClose}
          autoHideDuration={snackbar.duration}
        >
          <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </div>
  )
}

/**
 * Component to create a content list component
 * @param {Object} props The props
 * @param {Map} props.data The data state
 * @param {Map} props.location The location state
 * @param {String} props.storeKey The store key
 */
function ContentListV2 (props) {
  const { location, data } = props
  const auth = useSelector(selectAuth)
  const config = useSelector(selectConfig)
  const server = config.getIn(['data', 'servers', 'graphql'])
  const removeTypenameLink = removeTypenameFromVariables()
  const httpLink = new HttpLink({
    uri: `${server}/graphql`,
    headers: {
      authorization: createAuthorization(auth),
    },
  })
  const client = new ApolloClient({
    link: from([removeTypenameLink, httpLink]),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            ContentList: {
              ...offsetLimitPagination(),
              read (existing, { args: { offset, limit } }) {
                return existing && existing.slice(offset, offset + limit)
              },
            },
          },
        },
      },
    }),
  })

  useEffect(() => {
    const error = getViewError({
      data,
      storeKey: CONTENT_STORE_KEY,
      options: locationToOptions(location),
    })
    if (error) {
      alert(error)
    }
  }, [data, location])

  return (
    <Container className="content-list-data-table content-list-data-table--list" fluid={true}>
      <ApolloProvider client={client}>
        <ContentListRows location={location} />
      </ApolloProvider>
    </Container>
  )
}

ContentListV2.propTypes = {
  data: ImmutablePropTypes.map.isRequired,
  location: ImmutablePropTypes.map.isRequired,
}

ContentListV2.defaultProps = {
  storeKey: CONTENT_STORE_KEY,
}

export default compose(connectRedux((state) => _pick(state, ['data', 'location'])))(ContentListV2)
